import React, { useState, useEffect } from 'react';
import { Lock, Unlock, Banknote, AlertCircle, History, CheckCircle2 } from 'lucide-react';
import api from '../services/api';

export default function RegisterManager({ onSessionChange }) {
  const [activeSession, setActiveSession] = useState(null);
  const [openingCash, setOpeningCash] = useState('');
  const [actualCash, setActualCash] = useState('');
  const [notes, setNotes] = useState('');
  const [loading, setLoading] = useState(true);
  const [isProcessing, setIsProcessing] = useState(false);

  useEffect(() => {
    fetchSession();
  }, []);

  const fetchSession = async () => {
    try {
      const res = await api.get('/shifts/active');
      setActiveSession(res.data);
      if (onSessionChange) onSessionChange(res.data);
      setLoading(false);
    } catch (err) {
      console.error(err);
      setLoading(false);
    }
  };

  const openRegister = async (e) => {
    e.preventDefault();
    setIsProcessing(true);
    try {
      const res = await api.post('/shifts/open', { openingCash });
      setActiveSession(res.data);
      if (onSessionChange) onSessionChange(res.data);
      setOpeningCash('');
    } catch (err) {
      alert(err.response?.data?.error || 'Failed to open register');
    } finally {
      setIsProcessing(false);
    }
  };

  const closeRegister = async (e) => {
    e.preventDefault();
    setIsProcessing(true);
    try {
      await api.post('/shifts/close', { actualCash, notes });
      setActiveSession(null);
      if (onSessionChange) onSessionChange(null);
      setActualCash('');
      setNotes('');
    } catch (err) {
      alert(err.response?.data?.error || 'Failed to close register');
    } finally {
      setIsProcessing(false);
    }
  };

  if (loading) return null;

  if (!activeSession) {
    return (
      <div className="fixed inset-0 bg-gray-900/90 backdrop-blur-md flex items-center justify-center z-[60] p-4">
        <div className="bg-theme-card rounded-3xl shadow-2xl w-full max-w-md overflow-hidden animate-fade-in-up border border-theme">
          <div className="p-8 flex flex-col items-center text-center" style={{ backgroundColor: 'var(--brand)' }}>
            <div className="w-16 h-16 bg-white/20 rounded-2xl flex items-center justify-center mb-4">
              <Lock size={32} className="text-white" />
            </div>
            <h3 className="text-2xl font-black text-white uppercase tracking-tight">Register Locked</h3>
            <p className="text-white/70 mt-2 text-xs font-bold uppercase tracking-widest">Open a new session to start processing sales.</p>
          </div>
          
          <form onSubmit={openRegister} className="p-8 space-y-6">
            <div>
              <label className="block text-[10px] font-black text-theme-muted uppercase tracking-widest mb-2">Starting Cash Drawer Amount</label>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none text-theme-muted">
                  <Banknote size={20} />
                </div>
                <input 
                  type="number"
                  step="0.01"
                  required
                  placeholder="0.00"
                  className="w-full pl-12 pr-4 py-4 bg-theme-main border border-theme rounded-2xl focus:ring-2 focus:ring-brand outline-none font-black text-xl text-theme-main transition-all"
                  value={openingCash}
                  onChange={e => setOpeningCash(e.target.value)}
                />
              </div>
            </div>
            
            <button 
              type="submit"
              disabled={isProcessing}
              className="w-full bg-brand hover:bg-brand-light text-white py-4 rounded-2xl font-black text-sm uppercase tracking-widest shadow-lg transition-all flex items-center justify-center gap-3 disabled:opacity-70"
            >
              {isProcessing ? 'Opening...' : (
                <>
                  <Unlock size={22} /> Open Register
                </>
              )}
            </button>
          </form>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-emerald-500/10 border border-emerald-500/20 rounded-2xl p-4 flex items-center justify-between mb-6 shadow-sm">
      <div className="flex items-center gap-4">
        <div className="w-10 h-10 bg-emerald-500 rounded-full flex items-center justify-center text-white shadow-lg">
          <CheckCircle2 size={20} />
        </div>
        <div>
          <div className="text-sm font-black text-emerald-500 uppercase tracking-tight">Active Register Session</div>
          <div className="text-[10px] text-emerald-500/70 font-bold flex items-center gap-1 uppercase tracking-widest">
            <History size={12} /> Opened at {new Date(activeSession.openedAt).toLocaleTimeString()}
          </div>
        </div>
      </div>
      
      <button 
        onClick={() => {
          const proceed = window.confirm('Are you sure you want to close the register session?');
          if (proceed) {
            const cash = window.prompt('Enter final cash amount in drawer:');
            if (cash !== null) {
              api.post('/shifts/close', { actualCash: cash, notes: 'Day close' })
                .then(() => fetchSession());
            }
          }
        }}
        className="bg-theme-card text-emerald-500 border border-emerald-500/20 px-4 py-2 rounded-xl text-[10px] font-black uppercase tracking-widest hover:bg-emerald-500/10 transition-all shadow-sm"
      >
        Close Session
      </button>
    </div>
  );
}
